import type {
  Tournament,
  CreateTournamentRequest,
  UpdateTournamentRequest,
} from '@swiss-magic/shared-types';
import { ApiError, get, post, patch, del } from './core';

export function createTournament(data: CreateTournamentRequest): Promise<Tournament> {
  return post('/tournaments', data);
}

export function getTournaments(): Promise<Tournament[]> {
  return get('/tournaments');
}

// Story 100 — flip the spot-confirmation mode on a live tournament (owner).
export function updateSpotConfirmationMode(
  id: string,
  mode: 'manual' | 'auto',
): Promise<Tournament> {
  return patch(`/tournaments/${id}/spot-confirmation-mode`, {
    spotConfirmationMode: mode,
  });
}

export function getTournament(id: string): Promise<Tournament> {
  return get(`/tournaments/${id}`);
}

export function updateTournament(
  id: string,
  data: UpdateTournamentRequest,
  force?: boolean,
): Promise<Tournament> {
  const url = force ? `/tournaments/${id}?force=true` : `/tournaments/${id}`;
  return patch(url, data);
}

export function deleteTournament(id: string, force = false): Promise<void> {
  // Story 107 — god-mode delete: force skips the backend's draft-only gate.
  return del(`/tournaments/${id}${force ? '?force=true' : ''}`);
}

export function publishTournament(
  id: string,
  openRegistration?: boolean,
): Promise<Tournament> {
  return post(`/tournaments/${id}/publish`, { openRegistration });
}

export function unpublishTournament(id: string): Promise<Tournament> {
  return post(`/tournaments/${id}/unpublish`);
}

export function openRegistration(id: string): Promise<Tournament> {
  return post(`/tournaments/${id}/open-registration`);
}

export async function closeRegistration(
  id: string,
  options?: { force?: boolean; pendingAction?: 'approve' | 'reject' },
): Promise<{ tournament?: Tournament; pendingCount?: number }> {
  try {
    const tournament = await post<Tournament>(
      `/tournaments/${id}/close-registration`,
      options ?? {},
    );
    return { tournament };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const pendingCount =
        (err.body?.pendingCount as number | undefined) ??
        (err.body?.message as { pendingCount?: number } | undefined)?.pendingCount;
      return { pendingCount };
    }
    throw err;
  }
}

export function reopenRegistration(id: string): Promise<Tournament> {
  return post(`/tournaments/${id}/reopen-registration`);
}

export function completeTournament(id: string): Promise<Tournament> {
  return post(`/tournaments/${id}/complete-tournament`);
}

export function uncompleteTournament(id: string): Promise<Tournament> {
  return post(`/tournaments/${id}/uncomplete-tournament`);
}
